import { type Layout, type ItemCallback, Responsive, WidthProvider } from 'react-grid-layout';
import styled from 'styled-components';

import { useRGContext } from '../../contexts';
import { sortLayoutByY, transformLgToXxs } from './layoutTransformers';

const _ResponsiveGridLayout = WidthProvider(Responsive);
const ResponsiveGridLayout = _ResponsiveGridLayout as unknown as React.FC<any>;

// this fixes issues with the grid items jumping around when selected
//  see: https://github.com/react-grid-layout/react-grid-layout/issues/171
// this also (at least partly) removes the stupid animation that is triggered
//  when the render modes are switched
const GridItemMovementPatch = styled('div')<{ disableResize: boolean }>`
  .react-grid-layout {
    position: relative;
  }
  .react-grid-item {
    // transition: all 200ms ease;
    transition-property: left, top;
  }
  .react-grid-item.cssTransforms {
    transition-property: transform;
  }
  .react-grid-item.resizing {
    z-index: 1;
  }

  .react-grid-item.react-draggable-dragging {
    transition: none;
    z-index: 3;
  }

  .react-grid-item.react-grid-placeholder {
    background: #000;
    opacity: 0.1;
    // transition-duration: 100ms;
    z-index: 2;
    border-radius: 4px;
    -webkit-user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;
    -o-user-select: none;
    user-select: none;
    pointer-events: none; /* Allow events to pass through to elements below */
  }

  .react-grid-item:hover > .react-resizable-handle {
    z-index: 1000;
    display: ${({ disableResize }) => (disableResize ? 'none' : 'block')} !important;
    position: absolute;
    width: 25px;
    height: 25px;
    bottom: 0;
    right: 0;
    background: url('data:image/svg+xml;base64,PD94bWwgdmVyc2lvbj0iMS4wIiBzdGFuZGFsb25lPSJubyI/Pg08IS0tIEdlbmVyYXRvcjogQWRvYmUgRmlyZXdvcmtzIENTNiwgRXhwb3J0IFNWRyBFeHRlbnNpb24gYnkgQWFyb24gQmVhbGwgKGh0dHA6Ly9maXJld29ya3MuYWJlYWxsLmNvbSkgLiBWZXJzaW9uOiAwLjYuMSAgLS0+DTwhRE9DVFlQRSBzdmcgUFVCTElDICItLy9XM0MvL0RURCBTVkcgMS4xLy9FTiIgImh0dHA6Ly93d3cudzMub3JnL0dyYXBoaWNzL1NWRy8xLjEvRFREL3N2ZzExLmR0ZCI+DTxzdmcgaWQ9IlVudGl0bGVkLVBhZ2UlMjAxIiB2aWV3Qm94PSIwIDAgNiA2IiBzdHlsZT0iYmFja2dyb3VuZC1jb2xvcjojZmZmZmZmMDAiIHZlcnNpb249IjEuMSINCXhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyIgeG1sbnM6eGxpbms9Imh0dHA6Ly93d3cudzMub3JnLzE5OTkveGxpbmsiIHhtbDpzcGFjZT0icHJlc2VydmUiDQl4PSIwcHgiIHk9IjBweCIgd2lkdGg9IjZweCIgaGVpZ2h0PSI2cHgiDT4NCTxnIG9wYWNpdHk9IjAuMzAyIj4NCQk8cGF0aCBkPSJNIDYgNiBMIDAgNiBMIDAgNC4yIEwgNCA0LjIgTCA0LjIgNC4yIEwgNC4yIDAgTCA2IDAgTCA2IDYgTCA2IDYgWiIgZmlsbD0iIzAwMDAwMCIvPg0JPC9nPg08L3N2Zz4=');
    background-position: bottom right;
    padding: 0 3px 3px 0;
    background-repeat: no-repeat;
    background-origin: content-box;
    background-size: cover;
    box-sizing: border-box;
    cursor: se-resize;
  }
`;

const GridStyles = styled('div')`
  .grid-container {
    display: flex;

    justify-content: center;
    align-items: center;
  }

  .grid-item {
    width: 100%;
    height: 100%;
    position: relative;
    overflow: hidden;
  }

  .selected {
    z-index: 5;
    outline: 1px solid #cc0aac;
    box-shadow: 0 0 10px #cc0aac;
  }

  .react-resizable-hide .react-resizable-handle {
    display: none !important;
  }

  .react-grid-drag-handle {
    cursor: move;
  }
`;

export const ROW_CONTAINER_HEIGHT = 50;

const RGEditLayout = (props: {
  components: JSX.Element[];
  layouts: any;
  onLayoutChange: (layout: Layout[], allLayouts: Record<string, Layout[]>) => void;
  onResizeStart: ItemCallback;
  onResize: ItemCallback;
  onResizeStop: ItemCallback;
  onDragStart: ItemCallback;
  onDragStop: ItemCallback;
  onDrop: (layout: any, item: Layout, e: Event) => void;
  isDraggable: boolean;
  isResizable: boolean;
  isDroppable: boolean;
  disableResize: boolean;
  onBreakpointChange?: (breakpoint: 'xxs' | 'xs' | 'sm' | 'md' | 'lg') => void;
}) => {
  const { activeCompDimension } = useRGContext();

  const xxsLayout = transformLgToXxs(props.layouts);
  const sortedLayout = sortLayoutByY(props.layouts?.lg ?? []);
  const layouts = { lg: sortedLayout, sm: sortedLayout, xs: sortedLayout, xxs: xxsLayout };

  return (
    <GridItemMovementPatch disableResize={!!props.disableResize} id="movement-patch">
      <GridStyles id="grid-style-override">
        <ResponsiveGridLayout
          className="layout"
          rowHeight={ROW_CONTAINER_HEIGHT}
          breakpoints={{ lg: 1200, sm: 768, xs: 480, xxs: 0 }}
          cols={{ lg: 12, sm: 12, xs: 12, xxs: 1 }}
          layouts={layouts}
          onLayoutChange={props.onLayoutChange}
          onResizeStart={props.onResizeStart}
          onResize={props.onResize}
          onResizeStop={props.onResizeStop}
          onDrop={props.onDrop}
          onDragStart={props.onDragStart}
          onDragStop={props.onDragStop}
          onDropDragOver={() => activeCompDimension}
          draggableCancel={'.cancel-drag'}
          preventCollision={false} // To support auto rearrange while DnD
          isDraggable={props.isDraggable}
          isResizable={props.isResizable}
          isDroppable={props.isDroppable}
          margin={[0, 0]}
          autoSize={true}
          containerPadding={[3, 3]} // Just enough padding to see the selected pinkish border.
          draggableHandle=".react-grid-drag-handle"
          onBreakpointChange={props?.onBreakpointChange}
        >
          {props.components}
        </ResponsiveGridLayout>
      </GridStyles>
    </GridItemMovementPatch>
  );
};

export { RGEditLayout };
